import type { PosQuote, PosQuoteLine } from '@his/shared';

export interface QuotableCartLine {
  productId: string;
  qty: number;
  discountPct: number;
  priced: boolean;
  prescriptionItemId?: string;
}

export interface SaleLineBody {
  productId: string;
  qty: number;
  discountPct: number;
  prescriptionItemId?: string;
}

export interface PackageBadge {
  kind: 'in' | 'beyond';
  qty: number | null;
}

export interface CartLinePackage {
  badges: PackageBadge[];
  absorbedMinor: number;
  listNetMinor: number;
  billedNetMinor: number;
}

export interface CartPackageView {
  lines: (CartLinePackage | null)[];
  absorbedMinor: number;
  coveredMinor: number;
  listNetMinor: number;
  dueMinor: number;
  dueTaxMinor: number;
  holder: { name: string; allowanceLeftAfterMinor: number } | null;
}

export const QUOTE_DEBOUNCE_MS = 400;
export const QUOTE_FRESH_MS = 15_000;

export function saleLines(cart: readonly QuotableCartLine[]): SaleLineBody[] {
  return cart.map((line) => ({
    productId: line.productId,
    qty: line.qty,
    discountPct: line.discountPct,
    ...(line.prescriptionItemId ? { prescriptionItemId: line.prescriptionItemId } : {}),
  }));
}

export function quotableLines(cart: readonly QuotableCartLine[]): SaleLineBody[] | null {
  if (cart.length === 0) return null;
  const unready = cart.some(
    (line) => !line.priced || !Number.isFinite(line.qty) || line.qty <= 0 || !Number.isFinite(line.discountPct) || line.discountPct < 0 || line.discountPct > 100,
  );
  return unready ? null : saleLines(cart);
}

export function quoteSignature(lines: readonly SaleLineBody[] | null): string {
  return lines ? JSON.stringify(lines) : '';
}

function badgesOf(line: PosQuoteLine): PackageBadge[] {
  const split = line.inPackageQty > 0 && line.billedQty > 0;
  const badges: PackageBadge[] = [];
  if (line.inPackageQty > 0) badges.push({ kind: 'in', qty: split ? line.inPackageQty : null });
  if (line.billedQty > 0 && line.beyondPackage) badges.push({ kind: 'beyond', qty: split ? line.billedQty : null });
  return badges;
}

export function cartPackageView(quote: PosQuote | null | undefined, cartLength: number): CartPackageView | null {
  if (!quote) return null;
  const byIndex = new Map(quote.lines.map((line) => [line.index, line]));
  return {
    lines: Array.from({ length: cartLength }, (_, index) => {
      const line = byIndex.get(index);
      return line ? { badges: badgesOf(line), absorbedMinor: line.absorbedMinor, listNetMinor: line.listNetMinor, billedNetMinor: line.billedNetMinor } : null;
    }),
    absorbedMinor: quote.totals.absorbedMinor,
    coveredMinor: Math.max(0, quote.totals.listNetMinor - quote.totals.dueMinor),
    listNetMinor: quote.totals.listNetMinor,
    dueMinor: quote.totals.dueMinor,
    dueTaxMinor: quote.totals.dueTaxMinor,
    holder: quote.enrolment ? { name: quote.enrolment.name, allowanceLeftAfterMinor: quote.enrolment.allowanceLeftAfterMinor } : null,
  };
}

export function amountToCollect(params: { totalMinor: number; quoteDueMinor: number | null; serverDueMinor: number | null }): number {
  return params.serverDueMinor ?? params.quoteDueMinor ?? params.totalMinor;
}

export function refusedDue(details: readonly { path?: string; message: string }[], currentDueMinor: number): number | null {
  const covered = details.find((detail) => detail.path === 'packageDue');
  if (!covered) return null;
  const value = Number(covered.message);
  if (!Number.isFinite(value)) return null;
  const left = Math.max(0, value);
  return left === currentDueMinor ? null : left;
}
